import React, { useState, useEffect } from "react";
import { DashboardLayout } from "../../client/components/Sidebar";
import DefaultAdminLayout from "../../admin/layout/DefaultLayout";
import { type AuthUser } from "wasp/auth";
import {
  BreadcrumbNav,
  BreadcrumbItem,
} from "../../shared/components/BreadcrumbNav";

export interface IndividualViewPageLayoutProps {
  children: React.ReactNode;
  sidebarContent?: React.ReactNode;
  breadcrumbItems?: BreadcrumbItem[];
  currentPageUrl?: string;
  customTopBarContent?: React.ReactNode;
  layoutVariant?: "dashboard" | "admin";
  authUser?: AuthUser;
  actions?: React.ReactNode;
}

/**
 * Reusable layout component for individual item view pages.
 * Provides consistent structure with main content area and right sidebar.
 *
 * @example
 * ```tsx
 * <IndividualViewPageLayout
 *   breadcrumbItems={breadcrumbItems}
 *   currentPageUrl={window.location.href}
 *   sidebarContent={
 *     <>
 *       <ActionButtons />
 *       <DetailsSection />
 *     </>
 *   }
 * >
 *   <MainContent />
 * </IndividualViewPageLayout>
 * ```
 */
export default function IndividualViewPageLayout({
  children,
  sidebarContent,
  breadcrumbItems,
  currentPageUrl,
  customTopBarContent,
  layoutVariant = "dashboard",
  authUser,
  actions,
}: IndividualViewPageLayoutProps) {
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 1024;
  });

  useEffect(() => {
    const handleResize = () => setIsCompact(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Generate breadcrumb content if items are provided
  const breadcrumbContent =
    breadcrumbItems && currentPageUrl ? (
      <BreadcrumbNav items={breadcrumbItems} currentPageUrl={currentPageUrl} />
    ) : (
      customTopBarContent
    );

  const pageContent = (
    <div className="h-full bg-background flex flex-col">
      <div className="w-full flex-1">
        <div className="flex flex-col lg:flex-row h-full min-h-full">
          {/* Main Content */}
          <div className={`flex-1 min-w-0 px-8 py-6 ${isCompact && actions ? 'pb-32' : ''}`}>
            {children}
          </div>

          {/* Right Sidebar */}
          {(sidebarContent || (!isCompact && actions)) && (
            <div className={`w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-border flex flex-col bg-slate-50 overflow-y-auto ${isCompact ? "hidden" : ""}`}>
              {!isCompact && actions && (
                <div className="p-6 border-b border-border bg-white sticky top-0 z-10">
                  {actions}
                </div>
              )}
              {sidebarContent}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Bar for Actions */}
      {isCompact && actions && (
        <div className="fixed bottom-16 left-0 right-0 p-4 bg-white/80 backdrop-blur-md border-t border-border z-40 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
          <div className="max-w-md mx-auto">
            {actions}
          </div>
        </div>
      )}
    </div>
  );

  if (layoutVariant === "admin" && authUser) {
    return (
      <DefaultAdminLayout user={authUser}>{pageContent}</DefaultAdminLayout>
    );
  }

  return (
    <DashboardLayout 
      customTopBarContent={breadcrumbContent}
      forceSidebarCollapse={isCompact}
    >
      {pageContent}
    </DashboardLayout>
  );
}
